import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Text,
  TextField,
  Button,
  Spinner,
  BlockStack,
  InlineStack,
  Badge
} from '@shopify/polaris';
import { useClient } from '../contexts/ClientContext';
import { useAuth } from '../contexts/AuthContext';

interface ClientSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

const ClientSelectionModal: React.FC<ClientSelectionModalProps> = ({ open, onClose }) => {
  const { user } = useAuth();
  const { 
    clients, 
    loading, 
    error, 
    selectClient, 
    searchClients,
    retryFetchClients 
  } = useClient();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  console.log('ClientSelectionModal - open:', open, 'clients:', clients.length, 'loading:', loading, 'error:', error);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    const timeout = setTimeout(() => {
      searchClients(value);
    }, 300);
    
    setSearchTimeout(timeout);
  };

  const handleClientSelect = (client: any) => {
    console.log('Client selected:', client);
    try {
      selectClient(client);
      console.log('selectClient called, closing modal');
      onClose();
    } catch (error) {
      console.error('Error selecting client:', error);
    }
  };

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Only show for support managers
  if (user?.role !== 'support_manager') {
    return null;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Select Client"
      primaryAction={{
        content: 'Cancel',
        onAction: onClose,
      }}
      secondaryActions={[]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p" tone="subdued">
            Choose a client to manage their settings and data.
          </Text>

          <TextField
            label="Search clients"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Type client name..."
            autoComplete="off"
            clearButton
            onClearButtonClick={() => {
              setSearchQuery('');
              searchClients('');
            }}
          />

          {error && (
            <div style={{ padding: '12px', backgroundColor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
              <Text as="p" tone="critical">Error: {error}</Text>
              <Button onClick={retryFetchClients} variant="primary" size="slim">
                Retry
              </Button>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner size="small" />
              <Text as="p" tone="subdued">Loading clients...</Text>
            </div>
          )}

          {clients.length > 0 && (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {clients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    padding: '12px',
                    border: '1px solid #e1e3e5',
                    borderRadius: '6px',
                    marginBottom: '8px',
                    backgroundColor: 'white',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <InlineStack align="space-between">
                    <div>
                      <Text variant="bodyMd" as="p" fontWeight="semibold">
                        {client.name}
                      </Text>
                      <Text as="p" tone="subdued">
                        {client.current_plan || 'No plan'} • {new Date(client.created_at).toLocaleDateString()}
                      </Text>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {client.has_active_plan && (
                        <Badge tone="success">Active</Badge>
                      )}
                      {client.available_credits !== undefined && (
                        <Badge tone="warning">{`${client.available_credits} Credits`}</Badge>
                      )}
                      <Button
                        size="slim"
                        variant="primary"
                        onClick={() => handleClientSelect(client)}
                      >
                        Select
                      </Button>
                    </div>
                  </InlineStack>
                </div>
              ))}
            </div>
          )}

          {!loading && clients.length === 0 && searchQuery && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text as="p" tone="subdued">
                No clients found matching "{searchQuery}"
              </Text>
            </div>
          )}

          {!loading && clients.length === 0 && !searchQuery && (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Text as="p" tone="subdued">
                Start typing to search clients
              </Text>
            </div>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default ClientSelectionModal;
